import { fromEvent } from 'rxjs';
import { debounceTime, delay, filter, takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  Input,
  OnChanges,
  Optional,
  Self,
  SimpleChanges,
  SkipSelf,
} from '@angular/core';

import { NzThAddOnComponent } from 'ng-zorro-antd/table';

import { LifecycleHooks } from '@app/core/services/lifecycle-hooks.service';
import { NzSelectComponent } from 'ng-zorro-antd/select';

export type HeapData = Record<string, boolean | number | string>;

@Directive({ selector: '[adsptHeap]', providers: [LifecycleHooks] })
export class HeapDirective implements OnChanges, AfterViewInit {
  @Input()
  adsptHeap: string;

  @Input()
  heapData: HeapData = {};

  @Input()
  heapDisabled?: boolean;

  @Input()
  useParent?: boolean;

  @Input()
  useChild?: boolean;

  @Input()
  elementPath?: Partial<Record<'parent' | 'child' | 'next' | 'prev', string>>;

  @Input()
  debounceResolve?: boolean;

  private currentHeapClass: Map<Element, string> = new Map();

  private get filter(): HTMLElement {
    return this.element.querySelector('nz-filter-trigger');
  }

  private get element(): Element | null {
    const sourceElement = this.useParent
      ? this.el.nativeElement.parentElement
      : this.useChild
        ? this.el.nativeElement.firstElementChild
        : this.el.nativeElement;

    if (this.elementPath && sourceElement) {
      return Object.entries(this.elementPath).reduce((element, [type, selector]) => {
        if (!element) {
          return;
        }

        switch (type) {
          case 'parent':
            return element.closest(selector);
          case 'child':
            return element.querySelector(selector);
          case 'next':
            return element.nextElementSibling;
          case 'prev':
            return element.previousElementSibling;
        }
      }, sourceElement);
    } else {
      return sourceElement;
    }
  }

  constructor(
    private el: ElementRef<Element>,
    private lh: LifecycleHooks,
    @Optional()
    @Self()
    private th?: NzThAddOnComponent<unknown>,
    @Optional()
    @Host()
    @SkipSelf()
    private ns?: NzSelectComponent,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.adsptHeap || changes.heapData || changes.heapDisabled || changes.useParent || changes.elementPath) {
      if (this.th) {
        this.updateTableFilterClassNames();
      } else if (this.ns) {
        this.initSelectOptions();
      } else {
        this.generateClassNames();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.th) {
      this.setTableFilterListeners();
    }

    if (this.ns) {
      this.setSelectListeners();
    }

    this.generateClassNames();
  }

  private async setTableFilterListeners(): Promise<void> {
    await Promise.resolve();

    const filter = this.filter;

    if (filter) {
      fromEvent(filter, 'click', { capture: true })
        .pipe(delay(500), takeUntil(this.lh.destroy$))
        .subscribe(() => {
          this.updateTableFilterClassNames(true);
        });
    }
  }

  private initSelectOptions(): void {
    this.elementPath = { parent: '.ant-select-item-option' };
    this.useParent = true;
    this.debounceResolve = true;
  }

  private setSelectListeners(): void {
    this.ns.nzOpenChange
      .pipe(
        filter((open) => open),
        debounceTime(1),
        takeUntil(this.lh.destroy$),
      )
      .subscribe(() => this.generateClassNames());
  }

  private async updateTableFilterClassNames(setItemsEvent?: boolean): Promise<void> {
    await Promise.resolve();

    if (this.th.nzShowFilter) {
      const filter = this.filter;

      if (filter) {
        await this.generateClassNames(filter);

        const dropdown = document.querySelector('.ant-table-filter-dropdown');

        if (dropdown) {
          if (setItemsEvent) {
            fromEvent(dropdown, 'click')
              .pipe(takeUntil(this.lh.destroy$))
              .subscribe(() => {
                this.updateTableFilterClassNames();
              });
          }

          const items = dropdown.querySelectorAll('.ant-dropdown-menu-item');
          items.forEach((item, i) => {
            this.generateClassNames(item, `${this.adsptHeap}-item`, {
              ...(this.heapData || {}),
              filter: this.th.nzFilters[i].value,
              'disable/enable': item.classList.contains('ant-dropdown-menu-item-selected'),
            });
          });

          const ok = dropdown.querySelector('.ant-table-filter-dropdown-btns .ant-btn-primary');
          const reset = dropdown.querySelector('.ant-table-filter-dropdown-btns .ant-btn-link');

          if (ok) {
            this.generateClassNames(ok, `${this.adsptHeap}-ok`);
          }

          if (reset) {
            this.generateClassNames(reset, `${this.adsptHeap}-reset`);
          }
        }
      }
    }
  }

  private async generateClassNames(element?: Element, heapSelector?: string, heapData?: HeapData): Promise<void> {
    if (this.debounceResolve) {
      await Promise.resolve();
    }

    element = element || this.element;

    if (!element) {
      return;
    }

    heapSelector = heapSelector || this.adsptHeap;

    heapData = heapData || this.heapData || {};

    let currentClass = this.currentHeapClass.get(element);

    if (currentClass) {
      element.classList.remove(currentClass);
    }

    if (this.heapDisabled || !heapSelector) {
      this.currentHeapClass.delete(element);
    } else {
      const classes = Object.entries(heapData)
        .map(([key, value]) => {
          if (key.includes('/')) {
            return key.split('/')[value ? 0 : 1];
          } else if (value == undefined) {
            return null;
          } else if (value === true) {
            return key;
          } else if (value !== false) {
            return `${key}=${value}`;
          }
        })
        .filter((value) => value != null)
        .join('_');

      currentClass = `heap~${heapSelector}${classes ? `:${classes}` : ''}`.toLocaleLowerCase();
      element.classList.add(currentClass);
      this.currentHeapClass.set(element, currentClass);
    }
  }
}
